import { memo, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { CompanyCombobox, ContactCombobox } from "@/components/EntityComboboxes";
import { QuickAddCompanyDialog, QuickAddContactDialog } from "@/components/QuickAddDialogs";
import { Plus, Trash2, GripVertical, Pencil, Activity, ClipboardList, Search, X, LayoutGrid, CalendarDays, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Lock, Check, CheckCircle2, Filter } from "lucide-react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import DealDetail from "@/components/DealDetail";
import DatePicker from "@/components/DatePicker";
import { useDeals, useMoveDeal, useStages } from "@/hooks/useCrmQueries";
import { ACTIVITY_MAP, dayLabel, isOverdue } from "@/lib/activities";

const EMPTY = {
  title: "", value: 0, stage: "new_lead", contact_id: "", company_id: "", owner_id: "",
  expected_close: "", probability: 50, description: "", notes: "", deal_type: "",
};

const DEAL_TYPES = [
  { key: "k12",        label: "K12",        bg: "hsl(32 94% 44% / 0.14)",  fg: "hsl(32 94% 34%)"  },
  { key: "hei",        label: "HEI",        bg: "hsl(280 55% 45% / 0.14)", fg: "hsl(280 55% 40%)" },
  { key: "enterprise", label: "Enterprise", bg: "hsl(222 100% 33% / 0.10)", fg: "hsl(222 100% 33%)" },
];
const DEAL_TYPE_MAP = Object.fromEntries(DEAL_TYPES.map((t) => [t.key, t]));

const currency = (n) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 }).format(n || 0);

export default function Deals() {
  const qc = useQueryClient();
  const { user: currentUser } = useAuth();
  const { data: deals = [], refetch: refetchDeals } = useDeals();
  const { data: stages = [] } = useStages();
  const { data: contacts = [] } = useQuery({ queryKey: ["contacts"], queryFn: async () => (await api.get("/contacts")).data });
  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: async () => (await api.get("/companies")).data });
  const { data: users = [] } = useQuery({ queryKey: ["users"], queryFn: async () => (await api.get("/users")).data });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [openDealId, setOpenDealId] = useState(null);

  // 1. Search + 3. View mode + 4. Per-lane expand state
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    // 200 ms debounce — filter runs across all cards, so we don't recompute on every keystroke
    const t = setTimeout(() => setDebouncedSearch(search), 200);
    return () => clearTimeout(t);
  }, [search]);
  const [viewMode, setViewMode] = useState("kanban"); // "kanban" | "calendar"
  const [expandedLanes, setExpandedLanes] = useState({});
  const boardRef = useRef(null);

  // Nested quick-add dialogs
  const [quickCompanyOpen, setQuickCompanyOpen] = useState(false);
  const [quickContactOpen, setQuickContactOpen] = useState(false);

  const moveMutation = useMoveDeal();
  const openDeal = useMemo(() => deals.find((d) => d.id === openDealId) || null, [deals, openDealId]);

  const openCreate = () => {
    setEditingId(null);
    setForm({ ...EMPTY, stage: stages[0]?.key || "new_lead" });
    setDialogOpen(true);
  };
  const openEdit = (d) => {
    setEditingId(d.id);
    setForm({
      title: d.title || "",
      value: d.value || 0,
      stage: d.stage || "new_lead",
      contact_id: d.contact_id || "",
      company_id: d.company_id || "",
      owner_id: d.owner_id || "",
      expected_close: d.expected_close || "",
      probability: d.probability ?? 50,
      description: d.description || "",
      notes: d.notes || "",
      deal_type: d.deal_type || "",
    });
    setDialogOpen(true);
  };

  const canEditDeal = (deal) => {
    if (!currentUser) return false;
    if (currentUser.role === "admin") return true;
    return deal?.owner_id === currentUser.id;
  };

  // 4 mandatory fields — must all be filled for the Save button to enable.
  const formIsValid =
    !!form.title?.trim() &&
    !!form.company_id &&
    !!form.expected_close &&
    !!form.stage;

  const save = async () => {
    if (!formIsValid) {
      toast.error("Fill Title, Company, Stage and Expected close before saving.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        title: form.title.trim(),
        value: Number(form.value) || 0,
        stage: form.stage,
        contact_id: form.contact_id || null,
        company_id: form.company_id,
        owner_id: form.owner_id || null,
        expected_close: form.expected_close,
        probability: form.probability === "" ? null : Number(form.probability),
        description: form.description || "",
        notes: form.notes || "",
        deal_type: form.deal_type || null,
      };
      if (editingId) {
        await api.patch(`/deals/${editingId}`, payload);
        toast.success("Deal updated");
      } else {
        await api.post("/deals", payload);
        toast.success("Deal created");
      }
      setDialogOpen(false); setForm(EMPTY); setEditingId(null); refetchDeals();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this deal?")) return;
    try {
      await api.delete(`/deals/${id}`);
      toast.success("Deal removed");
      if (openDealId === id) setOpenDealId(null);
      refetchDeals();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const companyMap = useMemo(() => Object.fromEntries(companies.map((c) => [c.id, c.name])), [companies]);
  const contactMap = useMemo(() => Object.fromEntries(contacts.map((c) => [c.id, c.name])), [contacts]);
  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u.name])), [users]);

  // 1. Advance search — filter deals across title, company, contact, deal_type, notes, description, value
  const filteredDeals = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return deals;
    return deals.filter((d) => {
      const haystack = [
        d.title,
        companyMap[d.company_id],
        contactMap[d.contact_id],
        userMap[d.owner_id],
        d.deal_type,
        DEAL_TYPE_MAP[d.deal_type]?.label,
        d.notes,
        d.description,
        String(d.value || ""),
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(q);
    });
  }, [deals, debouncedSearch, companyMap, contactMap, userMap]);

  // Group deals by stage (already ordered by position from backend).
  const byStage = useMemo(() => {
    const m = {};
    stages.forEach((s) => (m[s.key] = []));
    filteredDeals.forEach((d) => { if (m[d.stage]) m[d.stage].push(d); });
    return m;
  }, [filteredDeals, stages]);

  const stageTotals = useMemo(() => {
    const t = {};
    stages.forEach((s) => {
      const arr = byStage[s.key] || [];
      t[s.key] = { count: arr.length, value: arr.reduce((sum, d) => sum + (d.value || 0), 0) };
    });
    return t;
  }, [byStage, stages]);

  // 2. Auto-scroll the board horizontally when dragging near either edge.
  useEffect(() => {
    if (!dragId || viewMode !== "kanban") return;
    const el = boardRef.current;
    if (!el) return;
    const EDGE = 90;      // px from left/right that triggers scroll
    const SPEED = 18;     // px per rAF frame
    let cursorX = null;
    let rafId = null;

    const onMove = (e) => { cursorX = e.clientX; };
    const tick = () => {
      if (cursorX != null) {
        const rect = el.getBoundingClientRect();
        if (cursorX - rect.left < EDGE) {
          el.scrollLeft -= SPEED;
        } else if (rect.right - cursorX < EDGE) {
          el.scrollLeft += SPEED;
        }
      }
      rafId = requestAnimationFrame(tick);
    };
    window.addEventListener("dragover", onMove);
    rafId = requestAnimationFrame(tick);
    return () => {
      window.removeEventListener("dragover", onMove);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [dragId, viewMode]);

  const performMove = async ({ stage, before_id, after_id }) => {
    if (!dragId) return;
    const deal = deals.find((d) => d.id === dragId);
    setDragId(null);
    if (!deal) return;
    // no-op: dropped on itself
    if (before_id === dragId || after_id === dragId) return;
    try {
      await moveMutation.mutateAsync({ id: dragId, stage, before_id, after_id });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-4 md:p-8 space-y-6" data-testid="deals-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="eyebrow mb-2">Sales pipeline</div>
          <h1 className="text-3xl tracking-tight font-medium">Deals</h1>
          <p className="text-sm text-muted-foreground mt-1">Drag between stages or reorder within a lane. Click a card to open it.</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) { setEditingId(null); setForm(EMPTY); } }}>
          <DialogTrigger asChild>
            <Button data-testid="new-deal-btn" onClick={openCreate} className="bg-primary hover:bg-primary/90 text-primary-foreground">
              <Plus className="h-4 w-4 mr-1.5" /> New deal
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle>{editingId ? "Edit deal" : "New deal"}</DialogTitle>
              <DialogDescription className="sr-only">
                Fill in the deal fields. Title, company, expected close and stage are required.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
              <div className="space-y-1.5">
                <Label>Title <span className="text-destructive">*</span></Label>
                <Input data-testid="deal-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Value (₹)</Label>
                  <Input data-testid="deal-value" type="number" min="0" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>Stage <span className="text-destructive">*</span></Label>
                  <select data-testid="deal-stage" className="h-9 border border-input rounded-md px-2 text-sm bg-background w-full"
                    value={form.stage} onChange={(e) => setForm({ ...form, stage: e.target.value })}>
                    {stages.map((s) => (<option key={s.key} value={s.key}>{s.label}</option>))}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Company <span className="text-destructive">*</span></Label>
                  <div className="flex gap-1.5">
                    <CompanyCombobox
                      companies={companies}
                      value={form.company_id}
                      onChange={(id) => setForm({ ...form, company_id: id })}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 shrink-0"
                      onClick={() => setQuickCompanyOpen(true)}
                      title="Add a new company"
                      data-testid="quick-add-company"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label>Contact</Label>
                  <div className="flex gap-1.5">
                    <ContactCombobox
                      contacts={contacts}
                      companyId={form.company_id}
                      value={form.contact_id}
                      onChange={(id) => setForm({ ...form, contact_id: id })}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="icon"
                      className="h-9 w-9 shrink-0"
                      onClick={() => setQuickContactOpen(true)}
                      title="Add a new contact"
                      data-testid="quick-add-contact"
                    >
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Deal type</Label>
                  <select
                    data-testid="deal-type"
                    className="h-9 border border-input rounded-md px-2 text-sm bg-background w-full"
                    value={form.deal_type}
                    onChange={(e) => setForm({ ...form, deal_type: e.target.value })}
                  >
                    <option value="">— None —</option>
                    {DEAL_TYPES.map((t) => (<option key={t.key} value={t.key}>{t.label}</option>))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Probability (%)</Label>
                  <Input data-testid="deal-probability" type="number" min="0" max="100" value={form.probability ?? ""}
                    onChange={(e) => setForm({ ...form, probability: e.target.value })} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label>Owner</Label>
                  <select data-testid="deal-owner" className="h-9 border border-input rounded-md px-2 text-sm bg-background w-full"
                    value={form.owner_id} onChange={(e) => setForm({ ...form, owner_id: e.target.value })}>
                    <option value="">— Me —</option>
                    {users.map((u) => (<option key={u.id} value={u.id}>{u.name}</option>))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label>Expected close <span className="text-destructive">*</span></Label>
                  <DatePicker
                    testId="deal-close"
                    value={form.expected_close}
                    onChange={(v) => setForm({ ...form, expected_close: v || "" })}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Description</Label>
                <Textarea data-testid="deal-description" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="deal-cancel">Cancel</Button>
              <Button onClick={save} disabled={saving || !formIsValid} data-testid="deal-save" className="bg-primary text-primary-foreground hover:bg-primary/90">
                {saving ? "Saving…" : editingId ? "Save" : "Create"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {/* Toolbar: search + view toggle */}
      <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
        <div className="relative flex-1 max-w-md" data-testid="deals-search-wrap">
          <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" strokeWidth={1.75} />
          <Input
            data-testid="deals-search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Filter deals — title, company, contact, notes…"
            className="pl-9 pr-8 h-9 bg-background"
          />
          {search && (
            <button
              data-testid="deals-search-clear"
              onClick={() => setSearch("")}
              className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 inline-flex items-center justify-center rounded hover:bg-accent"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          )}
        </div>

        <div className="inline-flex rounded-md border border-input p-0.5 bg-background self-start sm:self-auto" data-testid="deals-view-toggle">
          <button
            data-testid="view-toggle-kanban"
            onClick={() => setViewMode("kanban")}
            className={`inline-flex items-center gap-1.5 px-3 h-8 rounded text-sm transition-colors ${
              viewMode === "kanban" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <LayoutGrid className="h-4 w-4" strokeWidth={1.75} />
            <span className="hidden sm:inline">Kanban</span>
          </button>
          <button
            data-testid="view-toggle-calendar"
            onClick={() => setViewMode("calendar")}
            className={`inline-flex items-center gap-1.5 px-3 h-8 rounded text-sm transition-colors ${
              viewMode === "calendar" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
            }`}
          >
            <CalendarDays className="h-4 w-4" strokeWidth={1.75} />
            <span className="hidden sm:inline">Calendar</span>
          </button>
        </div>
      </div>

      {search && (
        <div className="text-xs text-muted-foreground" data-testid="deals-search-summary">
          Showing {filteredDeals.length} of {deals.length} deals matching &ldquo;{search}&rdquo;
        </div>
      )}

      {/* Kanban view */}
      {viewMode === "kanban" && (
        <div
          ref={boardRef}
          className="flex gap-4 overflow-x-auto pb-2 scroll-smooth"
          data-testid="kanban-board"
        >
          {stages.map((s) => (
            <KanbanLane
              key={s.key}
              stage={s}
              deals={byStage[s.key] || []}
              totals={stageTotals[s.key]}
              companyMap={companyMap}
              contactMap={contactMap}
              userMap={userMap}
              dragId={dragId}
              setDragId={setDragId}
              performMove={performMove}
              onOpen={setOpenDealId}
              onEdit={openEdit}
              onDelete={remove}
              canEditDeal={canEditDeal}
              expanded={!!expandedLanes[s.key]}
              onToggleExpand={() => setExpandedLanes((m) => ({ ...m, [s.key]: !m[s.key] }))}
            />
          ))}
        </div>
      )}

      {/* Calendar view */}
      {viewMode === "calendar" && (
        <DealsCalendarView
          deals={filteredDeals}
          companyMap={companyMap}
          userMap={userMap}
          onOpenDeal={setOpenDealId}
        />
      )}

      {openDeal && (
        <DealDetail
          deal={openDeal}
          users={users}
          companies={companies}
          contacts={contacts}
          onClose={() => setOpenDealId(null)}
          onEditDeal={(d) => openEdit(d)}
          onDeleteDeal={(d) => remove(d.id)}
        />
      )}

      {/* Nested quick-add: Company */}
      <QuickAddCompanyDialog
        open={quickCompanyOpen}
        onOpenChange={setQuickCompanyOpen}
        onCreated={(newCompany) => {
          qc.invalidateQueries({ queryKey: ["companies"] });
          setForm((f) => ({ ...f, company_id: newCompany.id }));
        }}
      />

      {/* Nested quick-add: Contact */}
      <QuickAddContactDialog
        open={quickContactOpen}
        onOpenChange={setQuickContactOpen}
        companies={companies}
        defaultCompanyId={form.company_id}
        onCreated={(newContact) => {
          qc.invalidateQueries({ queryKey: ["contacts"] });
          setForm((f) => ({
            ...f,
            contact_id: newContact.id,
            // If they created a contact tied to a company, prefill company too.
            company_id: f.company_id || newContact.company_id || "",
          }));
        }}
      />
    </div>
  );
}

/* -------------------- Kanban lane -------------------- */
const LANE_PAGE_SIZE = 20;

function KanbanLane({
  stage, deals, totals, companyMap, contactMap, userMap,
  dragId, setDragId, performMove, onOpen, onEdit, onDelete, canEditDeal,
  expanded, onToggleExpand,
}) {
  const [dragOverKey, setDragOverKey] = useState(null); // "start" | dealId (drop above that card) | "end"

  // 4. Scalability — collapse long lanes to first N cards with a "Show all" toggle.
  //    When dragging, always render full list so drop targets remain valid.
  const showAll = expanded || !!dragId || deals.length <= LANE_PAGE_SIZE;
  const visibleDeals = showAll ? deals : deals.slice(0, LANE_PAGE_SIZE);
  const hiddenCount = deals.length - visibleDeals.length;

  const handleDropAt = (target) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverKey(null);
    if (target === "end") {
      const last = deals[deals.length - 1];
      performMove({ stage: stage.key, before_id: null, after_id: last?.id || null });
    } else if (target === "start") {
      performMove({ stage: stage.key, before_id: deals[0]?.id || null, after_id: null });
    } else {
      // target is a deal id — drop ABOVE it
      const idx = deals.findIndex((d) => d.id === target);
      const above = deals[idx - 1];
      performMove({ stage: stage.key, before_id: target, after_id: above?.id || null });
    }
  };

  return (
    <div
      data-testid={`lane-${stage.key}`}
      className="kanban-lane w-80 shrink-0 flex flex-col max-h-[calc(100vh-16rem)]"
    >
      <div className="px-3 py-2.5 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="stage-dot" style={{ background: stage.color }} />
          <span className="text-sm font-medium">{stage.label}</span>
          <span className="text-xs text-muted-foreground" data-testid={`lane-count-${stage.key}`}>{totals?.count || 0}</span>
        </div>
        <div className="text-[11px] text-muted-foreground mono">{currency(totals?.value || 0)}</div>
      </div>
      <div className="p-2 overflow-y-auto flex-1">
        {/* Drop-slot at very top */}
        <DropSlot
          active={dragOverKey === "start"}
          onDragEnter={() => dragId && setDragOverKey("start")}
          onDragLeave={() => setDragOverKey((k) => (k === "start" ? null : k))}
          onDrop={handleDropAt("start")}
        />
        {visibleDeals.map((d) => (
          <div key={d.id}>
            <DealCard
              d={d}
              companyName={companyMap[d.company_id]}
              contactName={contactMap[d.contact_id]}
              ownerName={userMap[d.owner_id]}
              canEdit={canEditDeal ? canEditDeal(d) : true}
              onOpen={() => onOpen(d.id)}
              onEdit={() => onEdit(d)}
              onDelete={() => onDelete(d.id)}
              onDragStart={() => setDragId(d.id)}
              onDragEnd={() => setDragId(null)}
            />
            <DropSlot
              active={dragOverKey === d.id}
              onDragEnter={() => dragId && dragId !== d.id && setDragOverKey(d.id)}
              onDragLeave={() => setDragOverKey((k) => (k === d.id ? null : k))}
              onDrop={(e) => {
                // Drop below d — i.e., after this card
                e.preventDefault();
                e.stopPropagation();
                setDragOverKey(null);
                const idx = deals.findIndex((x) => x.id === d.id);
                const below = deals[idx + 1];
                performMove({ stage: stage.key, before_id: below?.id || null, after_id: d.id });
              }}
            />
          </div>
        ))}
        {hiddenCount > 0 && (
          <button
            data-testid={`lane-show-more-${stage.key}`}
            onClick={onToggleExpand}
            className="w-full mt-1 py-2 text-xs text-primary font-medium hover:bg-primary/5 rounded inline-flex items-center justify-center gap-1"
          >
            <ChevronDown className="h-3.5 w-3.5" />
            Show all {deals.length}
          </button>
        )}
        {expanded && deals.length > LANE_PAGE_SIZE && (
          <button
            data-testid={`lane-show-less-${stage.key}`}
            onClick={onToggleExpand}
            className="w-full mt-1 py-2 text-xs text-muted-foreground hover:bg-accent rounded inline-flex items-center justify-center gap-1"
          >
            <ChevronUp className="h-3.5 w-3.5" />
            Show less
          </button>
        )}
        {deals.length === 0 && (
          <div
            className="text-[11px] text-muted-foreground italic px-2 py-6 rounded border border-dashed border-border text-center"
            onDragOver={(e) => { if (dragId) e.preventDefault(); }}
            onDrop={handleDropAt("end")}
            data-testid={`empty-drop-${stage.key}`}
          >
            Drop deals here
          </div>
        )}
      </div>
    </div>
  );
}

function DropSlot({ active, onDragEnter, onDragLeave, onDrop }) {
  return (
    <div
      onDragOver={(e) => { e.preventDefault(); onDragEnter(); }}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      className={`h-2 rounded transition-colors duration-100 ${active ? "bg-primary/40 h-4 my-1" : "bg-transparent"}`}
    />
  );
}

/* -------------------- Deal card -------------------- */
const DealCard = memo(function DealCard({ d, companyName, contactName, ownerName, canEdit = true, onOpen, onEdit, onDelete, onDragStart, onDragEnd }) {
  const stop = (e) => e.stopPropagation();
  const typeChip = d.deal_type ? DEAL_TYPE_MAP[d.deal_type] : null;

  // Last activity chip logic (per user spec):
  //  - If most-recent scheduled activity is NOT completed → show its date; red when past.
  //  - If it IS completed → show "Last activity completed · <completed date>".
  const la = d.last_activity;
  let laLabel = null, laDateStr = null, laIsPast = false, laIsDone = false, LaIcon = null;
  if (la) {
    LaIcon = ACTIVITY_MAP[la.activity_type]?.icon || null;
    if (la.status === "completed") {
      laIsDone = true;
      laLabel = "Last activity completed";
      laDateStr = la.completed_date || la.scheduled_date;
    } else {
      laLabel = la.title || "Upcoming activity";
      laDateStr = la.scheduled_date;
      if (laDateStr) {
        const dt = new Date(laDateStr);
        if (!isNaN(dt) && dt.getTime() < Date.now()) laIsPast = true;
      }
    }
  }
  const fmtDate = (iso) => {
    if (!iso) return "";
    try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }); }
    catch { return ""; }
  };

  return (
    <div
      draggable={canEdit}
      onDragStart={(e) => {
        if (!canEdit) { e.preventDefault(); return; }
        e.dataTransfer.effectAllowed = "move";
        onDragStart();
      }}
      onDragEnd={onDragEnd}
      onClick={onOpen}
      data-testid={`deal-card-${d.id}`}
      className={`bg-card border border-border rounded-md p-3 cursor-pointer hover:border-primary/40 hover:shadow-sm transition-[border-color,box-shadow] duration-150 group mb-0.5 ${canEdit ? "" : "opacity-90"}`}
    >
      <div className="flex items-start gap-1.5">
        {canEdit ? (
          <GripVertical
            className="h-3.5 w-3.5 text-muted-foreground/50 mt-0.5 shrink-0 cursor-grab"
            onMouseDown={stop}
            onClick={stop}
          />
        ) : (
          <Lock
            className="h-3 w-3 text-muted-foreground/60 mt-0.5 shrink-0"
            data-testid={`deal-locked-${d.id}`}
            aria-label="Read-only — you are not the owner"
          />
        )}
        <div className="flex-1 min-w-0 space-y-1.5">
          <div className="flex items-start justify-between gap-2">
            <div className="text-sm font-medium leading-tight flex-1 min-w-0">{d.title}</div>
            {typeChip && (
              <span
                data-testid={`deal-type-chip-${d.id}`}
                className="text-[10px] font-medium uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
                style={{ background: typeChip.bg, color: typeChip.fg }}
              >
                {typeChip.label}
              </span>
            )}
          </div>
          <div className="text-xs text-muted-foreground truncate">{companyName || "—"}</div>
          {contactName && (<div className="text-[11px] text-muted-foreground truncate">{contactName}</div>)}
          <div className="flex items-center justify-between">
            <span className="text-sm font-semibold mono">{currency(d.value)}</span>
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider">{ownerName?.split(" ")[0] || "—"}</span>
          </div>
          {d.expected_close && (
            <div className="text-[10px] text-muted-foreground">Close · {new Date(d.expected_close).toLocaleDateString()}</div>
          )}
          {la && (
            <div
              data-testid={`deal-last-activity-${d.id}`}
              className={`flex items-center gap-1.5 text-[11px] pt-1 mt-1 border-t border-border ${
                laIsPast ? "text-destructive" : laIsDone ? "text-emerald-700 dark:text-emerald-500" : "text-muted-foreground"
              }`}
            >
              {laIsDone ? (
                <CheckCircle2 className="h-3 w-3" strokeWidth={1.75} />
              ) : LaIcon ? (
                <LaIcon className="h-3 w-3" strokeWidth={1.75} />
              ) : null}
              <span className="truncate">{laLabel}</span>
              <span className="ml-auto shrink-0 mono">{fmtDate(laDateStr)}</span>
            </div>
          )}
          <div className="flex items-center gap-3 pt-1 text-[10px] text-muted-foreground">
            <span className="inline-flex items-center gap-0.5"><Activity className="h-3 w-3" strokeWidth={1.75} />{d.activity_count || 0}</span>
            <span className="inline-flex items-center gap-0.5"><ClipboardList className="h-3 w-3" strokeWidth={1.75} />{d.open_tasks_count || 0}</span>
          </div>
        </div>
        {canEdit && (
          <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
            <button data-testid={`edit-deal-${d.id}`} onClick={(e) => { stop(e); onEdit(); }} className="text-muted-foreground hover:text-primary" aria-label="Edit deal">
              <Pencil className="h-3.5 w-3.5" />
            </button>
            <button data-testid={`delete-deal-${d.id}`} onClick={(e) => { stop(e); onDelete(); }} className="text-muted-foreground hover:text-destructive" aria-label="Delete deal">
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
});





/* -------------------- Deals Calendar View -------------------- */
// Event kinds & colours. Distinct hues per source.
const EVENT_STYLES = {
  deal:       { border: "hsl(222 100% 33%)",  bg: "hsl(222 100% 33% / 0.08)", label: "Deal",         plural: "Deals",       marker: "solid" },
  activity:   { border: "hsl(160 84% 30%)",   bg: "hsl(160 84% 30% / 0.10)",  label: "Activity",     plural: "Activities",  marker: "solid" },
  task_start: { border: "hsl(200 92% 44%)",   bg: "hsl(200 92% 44% / 0.10)",  label: "Task · Start", plural: "Task starts", marker: "dashed" },
  task_due:   { border: "hsl(0 74% 50%)",     bg: "hsl(0 74% 50% / 0.10)",    label: "Task · Due",   plural: "Task dues",   marker: "solid" },
};

// One legend chip per "source" — tasks share one chip that toggles both start & due.
const LEGEND = [
  { key: "deal",     styleKey: "deal",       label: "Deals" },
  { key: "activity", styleKey: "activity",   label: "Activities" },
  { key: "task",     styleKey: "task_due",   label: "Tasks (start & due)" },
];

const PRIORITY_LABEL = { urgent: "Urgent", important: "Important", medium: "Medium", low: "Low" };
const STATUS_LABEL   = { not_started: "Not Started", in_progress: "In Progress", completed: "Completed" };
const fmtDateShort = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return iso; }
};
const fmtDateTimeShort = (iso) => {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

/* -------------------- Calendar chip (lightweight, single global tooltip) -------------------- */
// Memoised so hovering one chip doesn't re-render the whole month.
const CalendarChip = memo(function CalendarChip({ event, onHover, onLeave }) {
  const style = EVENT_STYLES[event.kind];
  const isTaskStart = event.kind === "task_start";
  return (
    <button
      data-testid={`cal-event-${event.id}`}
      disabled={!event.onClick}
      onClick={event.onClick}
      onMouseEnter={(e) => onHover(event, e.currentTarget)}
      onMouseLeave={() => onLeave(event)}
      onFocus={(e) => onHover(event, e.currentTarget)}
      onBlur={() => onLeave(event)}
      className={`w-full text-left text-[11px] px-1.5 py-1 rounded truncate hover:brightness-95 transition-colors border-l-2 disabled:cursor-default ${
        isTaskStart ? "border-dashed" : ""
      }`}
      style={{ borderColor: style.border, backgroundColor: style.bg }}
    >
      <span className="font-medium">{event.title}</span>
    </button>
  );
});

function CalendarHoverBody({ event, companyMap, userMap, dealMap }) {
  const style = EVENT_STYLES[event.kind];
  const p = event.payload || {};
  return (
    <div>
      {/* Coloured header bar */}
      <div
        className="px-3 py-2 border-b"
        style={{ backgroundColor: style.bg, borderColor: style.border }}
      >
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full inline-block" style={{ background: style.border }} />
          <span className="text-[10px] uppercase tracking-wider font-semibold" style={{ color: style.border }}>
            {style.label}
          </span>
        </div>
        <div className="text-sm font-medium mt-1 leading-snug">{event.title}</div>
      </div>

      {/* Body */}
      <div className="p-3 space-y-1.5 text-[12px]">
        {event.kind === "deal" && (
          <>
            <Row label="Company" value={companyMap[p.company_id] || "—"} />
            <Row label="Value"   value={p.value ? currency(p.value) : "—"} mono />
            <Row label="Stage"   value={p.stage ? p.stage.replace(/_/g, " ") : "—"} />
            <Row label="Owner"   value={userMap?.[p.owner_id] || "—"} />
            <Row label="Close"   value={fmtDateShort(p.expected_close)} />
            {p.description && <Row label="Notes" value={p.description} multiline />}
          </>
        )}

        {event.kind === "activity" && (
          <>
            {p.deal_id && dealMap[p.deal_id] && (
              <Row label="Deal" value={dealMap[p.deal_id].title} />
            )}
            <Row label="Type"     value={p.activity_type || "—"} />
            <Row label="Status"   value={p.status || "—"} />
            <Row label="Assigned" value={userMap?.[p.assignee_id] || userMap?.[p.created_by] || "—"} />
            <Row label="When"     value={fmtDateTimeShort(p.scheduled_date)} />
            {p.description && <Row label="Notes" value={p.description} multiline />}
          </>
        )}

        {(event.kind === "task_start" || event.kind === "task_due") && (
          <>
            <Row
              label={event.kind === "task_start" ? "Start" : "Due"}
              value={fmtDateShort(event.kind === "task_start" ? p.start_date : p.due_date)}
            />
            {p.start_date && p.due_date && (
              <Row
                label={event.kind === "task_start" ? "Due" : "Start"}
                value={fmtDateShort(event.kind === "task_start" ? p.due_date : p.start_date)}
                muted
              />
            )}
            <Row label="Priority" value={PRIORITY_LABEL[p.priority] || p.priority || "—"} />
            <Row label="Status"   value={STATUS_LABEL[p.status] || p.status || "—"} />
            <Row label="Type"     value={p.type || "task"} />
            <Row label="Assigned" value={userMap?.[p.assignee_id] || userMap?.[p.created_by] || "—"} />
            {p.related_deal_id && dealMap[p.related_deal_id] && (
              <Row label="Deal" value={dealMap[p.related_deal_id].title} />
            )}
            {p.notes && <Row label="Notes" value={p.notes} multiline />}
          </>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, mono = false, muted = false, multiline = false }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-[10px] uppercase tracking-wider text-muted-foreground w-16 shrink-0 pt-0.5">{label}</span>
      <span
        className={`flex-1 ${mono ? "mono" : ""} ${muted ? "text-muted-foreground" : "text-foreground"} ${
          multiline ? "whitespace-pre-line line-clamp-3" : "truncate"
        }`}
      >
        {value || "—"}
      </span>
    </div>
  );
}


/* -------------------- Deals Calendar View -------------------- */
function DealsCalendarView({ deals, companyMap, userMap, onOpenDeal }) {
  const today = new Date();
  const [cursor, setCursor] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [kinds, setKinds] = useState({ deal: true, activity: true, task: true });

  // Fetch the current user's activities + tasks scheduled for the calendar.
  const { data: myEvents = { deals: [], activities: [], tasks: [] } } = useQuery({
    queryKey: ["calendar-my"],
    queryFn: async () => (await api.get("/calendar/my")).data,
  });

  // Build a deal lookup so tooltips can link an activity/task back to its deal by title.
  const dealMap = useMemo(() => {
    const m = {};
    for (const d of deals) m[d.id] = d;
    for (const d of (myEvents.deals || [])) if (!m[d.id]) m[d.id] = d;
    return m;
  }, [deals, myEvents]);

  // Single global tooltip state — one open at a time, portalled to body.
  // Debounced by 120ms so quick mouse-through doesn't spam mount/unmount.
  const [tip, setTip] = useState(null); // { event, rect }
  const openTimer = useRef(null);
  const closeTimer = useRef(null);
  const handleHover = (event, targetEl) => {
    if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; }
    if (openTimer.current)  clearTimeout(openTimer.current);
    openTimer.current = setTimeout(() => {
      const rect = targetEl.getBoundingClientRect();
      setTip({ event, rect });
    }, 120);
  };
  const handleLeave = () => {
    if (openTimer.current) { clearTimeout(openTimer.current); openTimer.current = null; }
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setTip(null), 80);
  };
  useEffect(() => () => {
    if (openTimer.current) clearTimeout(openTimer.current);
    if (closeTimer.current) clearTimeout(closeTimer.current);
  }, []);

  // Normalise every source into a common shape. `payload` carries the raw
  // record so the hover card can show rich details.
  const events = useMemo(() => {
    const out = [];
    if (kinds.deal) {
      for (const d of deals) {
        if (!d.expected_close) continue;
        const iso = String(d.expected_close).slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) continue;
        out.push({
          id: `deal-${d.id}`, kind: "deal", date: iso, title: d.title,
          subtitle: companyMap[d.company_id] || "",
          onClick: () => onOpenDeal(d.id),
          payload: d,
        });
      }
    }
    if (kinds.activity) {
      for (const a of (myEvents.activities || [])) {
        if (!a.scheduled_date) continue;
        const iso = String(a.scheduled_date).slice(0, 10);
        if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) continue;
        out.push({
          id: `act-${a.id}`, kind: "activity", date: iso, title: a.title || a.activity_type,
          subtitle: a.activity_type, dealId: a.deal_id,
          onClick: a.deal_id ? () => onOpenDeal(a.deal_id) : undefined,
          payload: a,
        });
      }
    }
    if (kinds.task) {
      for (const t of (myEvents.tasks || [])) {
        // Emit two events: one for start_date, one for due_date. Distinct colours.
        if (t.start_date) {
          const iso = String(t.start_date).slice(0, 10);
          if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
            out.push({
              id: `task-start-${t.id}`, kind: "task_start", date: iso, title: t.title,
              subtitle: `Start · ${PRIORITY_LABEL[t.priority] || t.priority || "medium"}`,
              payload: t,
            });
          }
        }
        if (t.due_date) {
          const iso = String(t.due_date).slice(0, 10);
          if (/^\d{4}-\d{2}-\d{2}$/.test(iso)) {
            out.push({
              id: `task-due-${t.id}`, kind: "task_due", date: iso, title: t.title,
              subtitle: `Due · ${PRIORITY_LABEL[t.priority] || t.priority || "medium"}`,
              payload: t,
            });
          }
        }
      }
    }
    return out;
  }, [deals, companyMap, myEvents, kinds, onOpenDeal]);

  // Group events by day
  const eventsByDay = useMemo(() => {
    const m = {};
    for (const e of events) (m[e.date] = m[e.date] || []).push(e);
    return m;
  }, [events]);

  // Undated deals (only when the deal-kind filter is on)
  const undated = useMemo(
    () => (kinds.deal ? deals.filter((d) => !d.expected_close) : []),
    [deals, kinds.deal],
  );

  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const startPad = firstDay.getDay();               // Sunday=0
  const daysInMonth = lastDay.getDate();
  const cells = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  while (cells.length % 7 !== 0) cells.push(null);

  const monthLabel = cursor.toLocaleString("en-IN", { month: "long", year: "numeric" });
  const isToday = (day) =>
    day && year === today.getFullYear() && month === today.getMonth() && day === today.getDate();

  const eventsThisMonth = events.filter((e) => {
    const dt = new Date(e.date);
    return dt.getFullYear() === year && dt.getMonth() === month;
  });
  const dealValueThisMonth = eventsThisMonth
    .filter((e) => e.kind === "deal")
    .reduce((s, e) => {
      const d = deals.find((x) => `deal-${x.id}` === e.id);
      return s + (d?.value || 0);
    }, 0);

  const toggleKind = (k) => setKinds((s) => ({ ...s, [k]: !s[k] }));

  return (
    <div className="space-y-4" data-testid="deals-calendar">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline" size="icon" className="h-9 w-9"
            data-testid="cal-prev-month"
            onClick={() => setCursor(new Date(year, month - 1, 1))}
            aria-label="Previous month"
          ><ChevronLeft className="h-4 w-4" /></Button>
          <Button
            variant="outline" size="icon" className="h-9 w-9"
            data-testid="cal-next-month"
            onClick={() => setCursor(new Date(year, month + 1, 1))}
            aria-label="Next month"
          ><ChevronRight className="h-4 w-4" /></Button>
          <Button
            variant="outline" className="h-9"
            data-testid="cal-today"
            onClick={() => setCursor(new Date(today.getFullYear(), today.getMonth(), 1))}
          >Today</Button>
          <div className="ml-2 text-lg font-medium" data-testid="cal-month-label">{monthLabel}</div>
        </div>
        <div className="text-xs text-muted-foreground">
          <span data-testid="cal-month-count">{eventsThisMonth.length} events</span>
          {" · "}
          <span data-testid="cal-month-value">{currency(dealValueThisMonth)}</span>
        </div>
      </div>

      {/* Legend / kind filters */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="cal-legend">
        {LEGEND.map(({ key, styleKey, label }) => {
          const s = EVENT_STYLES[styleKey];
          return (
            <button
              key={key}
              data-testid={`cal-toggle-${key}`}
              onClick={() => toggleKind(key)}
              className={`inline-flex items-center gap-1.5 text-[11px] px-2.5 py-1 rounded-full border transition-colors ${
                kinds[key] ? "bg-background" : "opacity-40 bg-secondary"
              }`}
              style={{ borderColor: s.border }}
            >
              <span className="h-2 w-2 rounded-full inline-block" style={{ background: s.border }} />
              {label}
            </button>
          );
        })}
      </div>

      {/* Grid */}
      <div className="card-flat overflow-hidden">
        <div className="grid grid-cols-7 border-b border-border text-[11px] font-medium text-muted-foreground bg-secondary/40">
          {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map((d) => (
            <div key={d} className="px-2 py-2 text-center">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {cells.map((day, idx) => {
            const iso = day ? `${year}-${String(month + 1).padStart(2,"0")}-${String(day).padStart(2,"0")}` : null;
            const dayEvents = iso ? (eventsByDay[iso] || []) : [];
            return (
              <div
                key={idx}
                data-testid={day ? `cal-day-${iso}` : undefined}
                className={`min-h-[96px] border-b border-r border-border p-1.5 flex flex-col gap-1 ${
                  day ? "bg-background" : "bg-secondary/20"
                }`}
              >
                {day && (
                  <>
                    <div
                      data-testid={`cal-daynum-${iso}`}
                      className={`text-xs font-semibold ${isToday(day) ? "text-primary" : "text-foreground"}`}
                    >
                      {isToday(day) ? (
                        <span className="inline-flex items-center justify-center rounded-full bg-primary text-primary-foreground w-6 h-6 text-[11px]">
                          {day}
                        </span>
                      ) : day}
                    </div>
                    <div className="space-y-1 flex-1">
                      {dayEvents.slice(0, 3).map((e) => (
                        <CalendarChip
                          key={e.id}
                          event={e}
                          onHover={handleHover}
                          onLeave={handleLeave}
                        />
                      ))}
                      {dayEvents.length > 3 && (
                        <div className="text-[10px] text-muted-foreground px-1.5" data-testid={`cal-more-${iso}`}>
                          +{dayEvents.length - 3} more
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Undated deals */}
      {undated.length > 0 && (
        <div className="card-flat p-4" data-testid="cal-undated">
          <div className="text-sm font-medium mb-2">Without a due date <span className="text-muted-foreground">({undated.length})</span></div>
          <div className="flex flex-wrap gap-2">
            {undated.slice(0, 30).map((d) => (
              <button
                key={d.id}
                data-testid={`cal-undated-${d.id}`}
                onClick={() => onOpenDeal(d.id)}
                className="text-xs px-2 py-1 rounded border border-border hover:bg-accent transition-colors"
              >
                {d.title}
                {d.value ? <span className="text-muted-foreground"> · {currency(d.value)}</span> : null}
              </button>
            ))}
            {undated.length > 30 && (
              <span className="text-xs text-muted-foreground self-center">+{undated.length - 30} more</span>
            )}
          </div>
        </div>
      )}

      {/* Single portalled tooltip — one instance for the entire calendar. */}
      {tip && createPortal(
        <CalendarTooltip
          anchor={tip.rect}
          event={tip.event}
          companyMap={companyMap}
          userMap={userMap}
          dealMap={dealMap}
        />,
        document.body,
      )}
    </div>
  );
}

/** Fixed-position tooltip that positions itself relative to the anchor rect. */
function CalendarTooltip({ anchor, event, companyMap, userMap, dealMap }) {
  const TOOLTIP_W = 288; // 18rem
  const GAP = 8;
  // Prefer above; if not enough room, place below.
  const preferAbove = anchor.top > 240;
  // Clamp horizontally to viewport
  const rawLeft = anchor.left;
  const left = Math.max(8, Math.min(rawLeft, window.innerWidth - TOOLTIP_W - 8));
  const top = preferAbove ? anchor.top - GAP : anchor.bottom + GAP;
  const transformY = preferAbove ? "translateY(-100%)" : "";

  return (
    <div
      data-testid={`cal-hover-${event.id}`}
      className="fixed z-[60] rounded-md border bg-popover text-popover-foreground shadow-lg overflow-hidden pointer-events-none animate-in fade-in-0 zoom-in-95 duration-100"
      style={{ top, left, width: TOOLTIP_W, transform: transformY }}
    >
      <CalendarHoverBody event={event} companyMap={companyMap} userMap={userMap} dealMap={dealMap} />
    </div>
  );
}
